import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { formatDate } from "date-fns";
import { useAtomValue } from "jotai";
import { CircleArrowDown, Ellipsis, FileText } from "lucide-react";
import { useState } from "react";
import {
	type DocumentDTO,
	getApiUrl,
	getDocumentIdContentV2Path,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentActionsDrawer } from "web_myplann/Portfolio/Components/Document/DocumentActionsDrawer";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { clampLinesSx } from "web_myplann/Shared/Utils/sxUtils";
import { device } from "web_myplann/Utils/device";
import { HandleDocumentTypes } from "web_myplann/Utils/WebViewModel";

type Props = {
	item: DocumentDTO;
	activeTag?: string;
};

export const DocumentItem = ({ item, activeTag }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const { token, tokenVerify, authorizationType } = useAtomValue(sessionState);
	const downloadedDocuments = useAtomValue(downloadedDocumentsState);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isDrawerConcealed, setIsDrawerConcealed] = useState(false);

	const handleOpenDrawer = () => {
		setIsDrawerOpen(true);
		setIsDrawerConcealed(false);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	const handleOpenDocument = async () => {
		const headers = new Headers();
		headers.set("Authorization", `Bearer ${token}`);
		headers.set("token-verify", tokenVerify);
		if (authorizationType) {
			headers.set("Authorization-type", authorizationType);
		}

		const response = await fetch(
			`${getApiUrl()}${getDocumentIdContentV2Path(item.id)}`,
			{
				headers,
			},
		);

		const blob = await response.blob();
		const reader = new FileReader();

		reader.onloadend = () => {
			let fileName: string | undefined;
			const result = reader.result;
			if (typeof result === "string") {
				const base64data = result.split(",")[1];
				const contentDisposition = response.headers.get("Content-Disposition");
				if (contentDisposition) {
					const fileNameMatch =
						contentDisposition.match(/filename="?([^"]+)"?/);
					if (fileNameMatch?.[1]) {
						fileName = fileNameMatch[1];
					}
				}
				device.handleDocument({
					file: {
						base64: base64data,
						filename: fileName ?? item.title ?? "",
					},
					actionType: HandleDocumentTypes.OPEN,
				});
			}
		};
		reader.readAsDataURL(blob);
	};

	return (
		<Stack key={item.id}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				py={2}
				px={1.6}
				onClick={handleOpenDocument}
			>
				<Stack direction="row" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
					<Box
						sx={{
							flexShrink: 0,
						}}
					>
						<FileText color={theme.palette.text.primary} size={24} />
					</Box>
					<Stack sx={{ minWidth: 0 }}>
						<Typography
							fontSize={15}
							fontWeight={500}
							sx={{
								...clampLinesSx(2),
								width: "100%",
							}}
						>
							{item.title}
						</Typography>

						<Stack direction="row" alignItems="center" gap={0.4}>
							{!downloadedDocuments.documents.includes(item.filename) && (
								<CircleArrowDown size={16} color={theme.palette.primary.main} />
							)}
							<Typography color={theme.palette.deprecated.grey[500]}>
								{formatDate(new Date(item.createdAt ?? ""), DateFormat.Display)}
							</Typography>
						</Stack>
					</Stack>
				</Stack>
				<Stack pl={1}>
					<IconButton
						edge="end"
						onClick={(e) => {
							e.stopPropagation();
							handleOpenDrawer();
						}}
					>
						<Ellipsis size={16} color={theme.palette.deprecated.grey[500]} />
					</IconButton>
				</Stack>
			</Stack>
			<MyPlannDrawer
				open={isDrawerOpen}
				onClose={handleCloseDrawer}
				title={t(Resources.Portfolio.List.DocumentActionsDrawer.title)}
				concealed={isDrawerConcealed}
			>
				<DocumentActionsDrawer
					item={item}
					onClose={handleCloseDrawer}
					onOverlayOpen={() => setIsDrawerConcealed(true)}
					activeTag={activeTag}
					isDownloaded={downloadedDocuments.documents.includes(item.filename)}
				/>
			</MyPlannDrawer>
		</Stack>
	);
};
